import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import companyLogo from '../assets/company-logo.png';
import { useAuth } from '../contexts/AuthContext';
import './Navbar.css';

/** Desktop keyboard order: Home first, then bar left → right, including More Tools dropdown items */
const NAV_ARROW_ORDER = [
  '/',
  '/profile-builder',
  '/essay-builder',
  '/admissions-chatbot',
  '/roadmap',
  '/application-portal',
  '/resume-review',
  '/school-intel',
  '/odds-calculator',
  '/school-comparison',
  '/study-plan',
  '/calendar',
  '/forum',
  '/blog',
  '/account'
] as const;

function navArrowIndex(pathname: string): number {
  return NAV_ARROW_ORDER.findIndex((route) => {
    if (route === '/') return pathname === '/' || pathname === '';
    if (route === '/blog') return pathname.startsWith('/blog');
    return pathname === route;
  });
}

const Navbar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, loading: authLoading, signOut } = useAuth();

  const primaryNavItems = useMemo(
    () => [
      {
        to: '/profile-builder',
        label: 'Profile Builder',
        isActive: location.pathname === '/profile-builder'
      },
      {
        to: '/essay-builder',
        label: 'Essay Analysis',
        isActive: location.pathname === '/essay-builder'
      },
      {
        to: '/admissions-chatbot',
        label: 'Admissions Assistant',
        isActive: location.pathname === '/admissions-chatbot'
      },
      {
        to: '/roadmap',
        label: 'Applicant Roadmap',
        isActive: location.pathname === '/roadmap'
      },
      {
        to: '/application-portal',
        label: 'Application Portal',
        isActive: location.pathname === '/application-portal'
      }
    ],
    [location.pathname]
  );

  const blogItem = useMemo(
    () => ({
      to: '/blog',
      label: 'Blog',
      isActive: location.pathname.startsWith('/blog')
    }),
    [location.pathname]
  );

  const homeLinkItem = useMemo(
    () => ({
      to: '/',
      label: 'Home',
      isActive: location.pathname === '/'
    }),
    [location.pathname]
  );

  const moreToolItems = useMemo(
    () => [
      {
        to: '/resume-review',
        label: 'Resume Review',
        isActive: location.pathname === '/resume-review'
      },
      {
        to: '/school-intel',
        label: 'School Intel (Beta)',
        isActive: location.pathname === '/school-intel'
      },
      {
        to: '/odds-calculator',
        label: 'Admissions Odds',
        isActive: location.pathname === '/odds-calculator'
      },
      {
        to: '/school-comparison',
        label: 'School Comparison',
        isActive: location.pathname === '/school-comparison'
      },
      {
        to: '/study-plan',
        label: 'Study Planner',
        isActive: location.pathname === '/study-plan'
      },
      {
        to: '/calendar',
        label: 'Calendar',
        isActive: location.pathname === '/calendar'
      },
      {
        to: '/forum',
        label: 'Forums',
        isActive: location.pathname === '/forum'
      },
      blogItem
    ],
    [blogItem, location.pathname]
  );

  const isMoreToolsActive = useMemo(
    () => moreToolItems.some((i) => i.isActive),
    [moreToolItems]
  );

  // Mobile sheet: Home first; desktop More Tools omits Home (logo goes home).
  const mobileNavItems = useMemo(
    () => [homeLinkItem, ...primaryNavItems, ...moreToolItems],
    [homeLinkItem, moreToolItems, primaryNavItems]
  );

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const typingSelector =
      'textarea, input:not([type="checkbox"]):not([type="radio"]), select, [contenteditable="true"]';

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      if (isMobileMenuOpen) return;

      const el = e.target as HTMLElement | null;
      if (el?.closest(typingSelector)) return;
      if (el?.isContentEditable) return;

      if (NAV_ARROW_ORDER.length < 2) return;

      e.preventDefault();

      // Clicking logo/nav focuses it; arrow navigation changes route but not focus, so the old
      // element keeps a focus ring—blur first when switching via arrows.
      const ae = document.activeElement;
      if (ae instanceof HTMLElement && ae.closest('.navbar')) {
        ae.blur();
      }

      let idx = navArrowIndex(location.pathname);
      if (idx === -1) idx = 0;

      const nextIdx =
        e.key === 'ArrowRight'
          ? (idx + 1) % NAV_ARROW_ORDER.length
          : (idx - 1 + NAV_ARROW_ORDER.length) % NAV_ARROW_ORDER.length;
      navigate(NAV_ARROW_ORDER[nextIdx]);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [location.pathname, navigate, isMobileMenuOpen]);

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/" className="navbar-logo" aria-label="Deferredly home">
          <img src={companyLogo} alt="" className="navbar-logo-icon" />
          <span>Deferredly</span>
        </Link>

        <nav className="navbar-center" aria-label="Main pages">
          <ul className="navbar-menu-main">
            {primaryNavItems.map((item) => (
              <li key={item.to} className="navbar-item">
                <Link
                  to={item.to}
                  className={`navbar-link ${item.isActive ? 'active' : ''}`}
                >
                  {item.label}
                </Link>
              </li>
            ))}

            <li className={`navbar-item navbar-tools ${isMoreToolsActive ? 'active' : ''}`}>
              <button
                type="button"
                className="navbar-link navbar-tools-trigger"
                aria-haspopup="menu"
                aria-label="More tools"
              >
                More Tools
                <span className="navbar-tools-caret" aria-hidden="true" />
              </button>

              <ul className="navbar-tools-menu" role="menu" aria-label="Tools">
                {moreToolItems.map((item) => (
                  <li key={item.to} role="none">
                    <Link
                      to={item.to}
                      role="menuitem"
                      className={`navbar-dropdown-link ${item.isActive ? 'active' : ''}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </li>

          </ul>
        </nav>

        <div className="navbar-auth-desktop" aria-label="Account">
          {authLoading ? (
            <span className="navbar-auth-placeholder" aria-hidden="true">
              …
            </span>
          ) : user ? (
            <div className="navbar-auth-cluster">
              <Link
                to="/account"
                className={`navbar-link ${location.pathname === '/account' ? 'active' : ''}`}
              >
                Account
              </Link>
              <button type="button" className="navbar-link-button" onClick={() => void signOut()}>
                Log out
              </button>
            </div>
          ) : (
            <div className="navbar-auth-cluster">
              <Link
                to="/login"
                className={`navbar-link ${location.pathname === '/login' ? 'active' : ''}`}
              >
                Log in
              </Link>
              <Link
                to="/signup"
                className={`navbar-link navbar-link-primary ${location.pathname === '/signup' ? 'active' : ''}`}
              >
                Sign up
              </Link>
            </div>
          )}
        </div>

        <button
          type="button"
          className="navbar-hamburger"
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMobileMenuOpen}
          aria-controls="navbar-mobile-menu"
          onClick={() => setIsMobileMenuOpen((v) => !v)}
        >
          <span className="hamburger-lines" aria-hidden="true">
            <span className="hamburger-line" />
            <span className="hamburger-line" />
            <span className="hamburger-line" />
          </span>
        </button>
      </div>

      {isMobileMenuOpen && (
        <div
          className="navbar-mobile-overlay"
          role="presentation"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <div
        id="navbar-mobile-menu"
        className={`navbar-mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="navbar-mobile-header">
          <div className="navbar-mobile-brand">
            <img src={companyLogo} alt="" className="navbar-mobile-logo-icon" />
            <span>Deferredly</span>
          </div>
          <button
            type="button"
            className="navbar-mobile-close"
            aria-label="Close menu"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            ×
          </button>
        </div>

        <nav className="navbar-mobile-nav" aria-label="Mobile navigation">
          {mobileNavItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`navbar-mobile-link ${item.isActive ? 'active' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {item.label}
            </Link>
          ))}
          <div className="navbar-mobile-auth" role="group" aria-label="Account">
            {authLoading ? null : user ? (
              <>
                <Link
                  to="/account"
                  className={`navbar-mobile-link ${location.pathname === '/account' ? 'active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Account
                </Link>
                <button
                  type="button"
                  className="navbar-mobile-link navbar-mobile-link-button"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    void signOut();
                  }}
                >
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className={`navbar-mobile-link ${location.pathname === '/login' ? 'active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Log in
                </Link>
                <Link
                  to="/signup"
                  className={`navbar-mobile-link ${location.pathname === '/signup' ? 'active' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Sign up
                </Link>
              </>
            )}
          </div>
        </nav>
      </div>
    </nav>
  );
};

export default Navbar;
